import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import AdminLayout from "../../../components/layout/AdminLayout";
import Button from "../../../components/button/Button";
import Select from "../../../components/select/Select";
import InputText from "../../../components/inputText/InputText";
import Loader from "../../../components/modal/Loader";
import ContentList from "../../../components/contentList/ContentList";
import Pagination from "../../../components/pagination/Pagination";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getToken } from "../../../services/auth";
import Modal from "../../../components/modal/Modal";
import { config } from '../../../config/env';

const Blogs = () => {
  const navigate = useNavigate();
  const statusList = [
    { id: 0, name: "All" },
    { id: 1, name: "Draft" },
    { id: 2, name: "Published" },
    { id: 3, name: "Archived" },
  ];

  const [searchParams] = useSearchParams();

  const [topics, setTopics] = useState([]);
  const [blogs, setBlogs] = useState([]);
  
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState();
  const [selectedStatus, setSelectedStatus] = useState();
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [itemsPerPage] = useState(15);
  const [currentPage, setCurrentPage] = useState(
    searchParams.get("page") ? parseInt(searchParams.get("page")) : 1
  );

  const [modalIcon, setModalIcon] = useState("question");
  const [modalTitle, setModalTitle] = useState("");
  const [modalSubText, setModalSubText] = useState("");
  const [confirmButtonText, setConfirmButtonText] = useState("");
  const [confirmButtonAction, setConfirmButtonAction] = useState(null);
  const [resetCount, setResetCount] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("confirm");

  // Function to update the query parameters in the URL
  const updateQuery = (pairs) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    pairs.forEach((element) => {
      newSearchParams.set(element[0], element[1]);
    });
    navigate(`?${newSearchParams.toString()}`, { replace: true });
  };

  // Function to fetch demos from the API
  const fetchBlogs = async (signal) => {
    try {
      setIsLoading(true);
      setBlogs([]);
      setError("");
      let url = new URL(
        `${config.apiBaseUrl}/api/v1/admin/content/blog/`
      );

      if (searchParams.get("page")) {
        url.searchParams.append("page", currentPage);
      }

      url.searchParams.append("limit", itemsPerPage);

      if (searchParams.get("topic_id") && searchParams.get("topic_id") !== "0") {
        url.searchParams.append("topic_id", searchParams.get("topic_id"));
      } 

      if (searchParams.get("content_status_id") && searchParams.get("content_status_id") !== "0") {
        url.searchParams.append("content_status_id", searchParams.get("content_status_id"));
      }

      if (searchParams.get("search")) {
        url.searchParams.append("search", searchParams.get("search"));
        setSearch(searchParams.get("search"));
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await getToken()}`
        },
        signal: signal ? signal : null,
      });
      if (response.status === 200) {
        const data = await response.json();
        setBlogs(data);
      } else {
        const data = await response.json();
        setError(data.message);
      }

      setIsLoading(false);
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Fetch abort")
      } else {
        console.error("Error fetching research papers:", error);
      }
    }
  };

  // Function to handle topic change
  const topicChangeHandler = (topic) => {
    setSelectedTopic(topic); 
    updateQuery([["topic_id", topic.id]]);
  };

  // Function to handle status change
  const statusChangeHandler = (status) => {
    setSelectedStatus(status);
    updateQuery([["content_status_id", status.id]]);
  };

  // Function to handle search input change
  useEffect(() => {
    updateQuery([["search", search]]);
  }, [search]);

  // Functions to handle page change
  useEffect(() => {
    const controller = new AbortController();
    fetchBlogs(controller.signal);

    return () => {
      controller.abort();
    }
  }, [currentPage,resetCount]);

  const pageChangeHandler = (page) => {
    setCurrentPage(page);
    updateQuery([["page", page]]);
  };

  const addBlogHandler = () => {
    navigate("/ava-admin/blogs/add");
  };

  // Fetch topics and demos on component mount
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const response = await fetch(
          `${config.apiBaseUrl}/api/v1/admin/topics/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${await getToken()}`
            },
          }
        );
        const data = await response.json();
        setTopics([{ id: 0, name: "All" }, ...data.data]);
        if (searchParams.get("topic_id")) {
          const topic = data.data.find(
            (t) => t.id === parseInt(searchParams.get("topic_id"))
          );
          if (topic) {
            setSelectedTopic(topic);
          }
        }
      } catch (error) {
        console.error("Error fetching topics:", error);
      }
    };

    fetchTopics();
    fetchBlogs();
  }, []);


  // Function to handle filter button click
  const filterHandler = () => {
    let x = [];
    if (selectedTopic) {
      x.push(["topic_id", selectedTopic.id]);
    }
    if (selectedStatus) {
      x.push(["content_status_id", selectedStatus.id]);
    }
    x.push(["search", search]);
    
    if (currentPage !== 1) {
      x.push(["page", 1]);
      updateQuery(x);
      setCurrentPage(1);
    } else {
      updateQuery(x);
      fetchBlogs();
    }
  }

  const resetFilterHandler = () => {
    setSelectedTopic({ id: 0, name: "All" })
    setSelectedStatus({ id: 0, name: "All" })
    setSearch("")
    updateQuery([["topic_id", 0], ["content_status_id", 0], ["search", ""]])
    setResetCount(c => c + 1);
  };
  
  
  const handleArchiveClick = (item) => {
    const isArchived = item.content_status?.id === 3;
    setModalMode("confirm");
    setModalIcon("question");
    setModalTitle(`Confirm ${isArchived ? "Unarchive" : "Archive"}`);
    setModalSubText(
      `Are you sure you want to ${isArchived ? "unarchive" : "archive"} this blog?`
    );
    setConfirmButtonText("Confirm");
    setConfirmButtonAction(() => () => confirmArchive(item, isArchived));
    setShowModal(true);
  };

  const confirmArchive = async (item, isArchived) => {
    setIsLoading(true);
  
    const action = isArchived ? "unarchive" : "archive";
    const endpoint = `${config.apiBaseUrl}/api/v1/admin/content/blog/${item.id}/${action}`;
  
    try {
      const token = await getToken();
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
  
      setModalMode("feedback");
  
      if (response.ok) {
        setBlogs(prev => ({
          ...prev,
          data: prev.data.map(p =>
            p.id === item.id
              ? {
                  ...p,
                  content_status: {
                    id: isArchived ? 1 : 3,
                    name: isArchived ? "Draft" : "Archived",
                  },
                }
              : p
          )
        }));
        setModalIcon("success");
        setModalTitle("Success");
        setModalSubText(
          `Blog was successfully ${isArchived ? "unarchived" : "archived"}.`
        );
        setConfirmButtonText("OK");
        setConfirmButtonAction(() => () => resetModal());
        } else {
            let errorMessage = "Something went wrong.";

            try {
                const errorData = await response.json();
                errorMessage = errorData?.message || JSON.stringify(errorData);
            } catch (jsonErr) {
                try {
                    errorMessage = await response.text();
                    } catch (textErr) {
                        errorMessage = "Unable to read error message.";
                    }
                }

            setModalMode("feedback");
            setModalIcon("error");
            setModalTitle("Error");
            setModalSubText(errorMessage);
            setConfirmButtonText("Close");
            setConfirmButtonAction(() => () => resetModal());
        }
    } catch (error) {
      setModalIcon("error");
      setModalTitle("Error");
      setModalSubText(error.message || "Something went wrong.");
      setConfirmButtonText("Close");
      setConfirmButtonAction(() => () => resetModal());
    } finally {
      setIsLoading(false);
    }
  };
  
  const resetModal = () => {
    setShowModal(false);
    setConfirmButtonAction(null);
    setModalMode("confirm");
  };

  return (
    <div>
      <Helmet>
        <title>Avanade Research - Admin</title>
      </Helmet>
      <div page="blogs">
        <div className="w-[calc(100%-40px)] min-h-[calc(100%-40px)] m-[20px] bg-white rounded-2xl p-10 shadow-lg text-sm">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex w-full">
              <div className="block w-full">
                <div className="text-lg font-semibold">Blogs</div>
                <div className="text-gray-600">
                  Visual presentation to showcase the output of studies and experiments.
                </div>
              </div>
              <div className="content-center">
                <div className="text-nowrap">
                  <Button
                    buttonClassName="py-2 px-5"
                    onClick={addBlogHandler}
                  >
                    Add Blog
                  </Button>
                </div>
              </div>
            </div>


            <div className="mt-10">
              <div className="flex font-semibold mb-10">
                {topics.length > 0 && (
                  <div className="w-[20%] mr-5">
                    <Select
                      items={topics}
                      label="Topic"
                      itemChangeHandler={topicChangeHandler}
                      selectedItemId={selectedTopic?.id} 
                    />
                  </div>
                )}
                <div className="w-[15%] mr-5">
                  <Select
                    items={statusList}
                    label="Status"
                    itemChangeHandler={statusChangeHandler}
                    selectedItemId={selectedStatus?.id}
                  />
                </div>
                <div className="w-[30%]">
                  <InputText
                    label="Search"
                    value={search}
                    onBlur={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && document.activeElement.blur()
                    }
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-12 ml-5">
                  <div className="w-[10%]">
                    <Button
                      className="mt-7"
                      buttonClassName="py-2 px-5"
                      onClick={filterHandler}
                    >
                      Filter
                    </Button>
                  </div>
                  <div className="w-[10%] ml-5">
                    <Button
                      className="mt-7"
                      buttonClassName="py-2 px-5"
                      onClick={resetFilterHandler}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              </div>
                {blogs.data && blogs.data.length === 0 && (
                <div className="flex justify-center items-center h-[200px]">
                  <div className="text-gray-500 text-lg font-semibold">
                    No blogs found
                  </div>
                </div>
              )}
                {error !== "" && (
                <div className="flex justify-center items-center h-[200px]">
                  <div className="text-gray-500 text-lg font-semibold">
                    {error}
                  </div>
                </div>
              )}
              <div className="h-[1px] bg-gray-300 w-full mt-5"></div>

              <ContentList
                archivedCallback={handleArchiveClick}
                contents={blogs}
                contentType="blogs"
                isAdmin={true}
                showContentStatus={true}
              />
              {showModal && (
                <Modal
                  title={modalTitle}
                  subtext={modalSubText}
                  cancelButton={modalMode === "confirm"}
                  confirmButtonText={confirmButtonText}
                  confirmButtonAction={confirmButtonAction}
                  icon={modalIcon}
                  loading={isLoading}
                  onCancel={resetModal}
                />
              )}
                                   
              <Pagination
                currentPage={currentPage}
                totalItems={blogs.count}
                itemsPerPage={itemsPerPage}
                setCurrentPage={pageChangeHandler}
              />
            </div>
          </div>
        </div>
      </div>
      {isLoading && <Loader />}
    </div>
  );
};

export default Blogs;
